import {Circle, Group, Line} from "react-konva";
import {gridToImage} from "@/dungeons/geometry/Coordinates.ts";
import {transitionArrivalOnSourceImage} from "@/dungeons/geometry/TransitionArrival.ts";
import TransitionArrivalMarker from "@/dungeons/geometry/TransitionArrivalMarker.tsx";
import {transitionAreaCenter, validTransitionPolygon} from "@/dungeons/geometry/TransitionPolygon.ts";
import type {LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import type {GeometryEditorController} from "./useGeometryEditorController.ts";
import {doorAppearance, doorHitPoints} from "./DoorAppearance.ts";
import GeometrySnapPreviewOverlay from "./GeometrySnapPreviewOverlay.tsx";
import useSnappedGeometryDrag from "./useSnappedGeometryDrag.ts";
import {moveWallPoint, moveDoorPoint} from "./GeometryDocumentOperations.ts";

export default function SelectedGeometryOverlay({controller, targetGeometry, targetLevelName, snappingEnabled, zoom, onPreviewGeometry}: {
    controller: GeometryEditorController;
    targetGeometry?: LevelGeometryDocument;
    targetLevelName?: string;
    snappingEnabled: boolean;
    zoom: number;
    onPreviewGeometry?: (document: LevelGeometryDocument | undefined) => void;
}) {
    const {document, selectedWall, selectedDoor, selectedTransition, commands} = controller;
    const drag = useSnappedGeometryDrag({geometry: document, zoom, snappingEnabled});

    if (selectedWall) {
        const points = selectedWall.points.map((point, pointIndex) => gridToImage(
            drag.pointFor(`wall:${selectedWall.id}:${pointIndex}`, point), document.registration,
        ));
        return <>
            <Line points={points.flatMap(point => [point.x, point.y])} stroke="#22d3ee"
                  strokeWidth={4 / zoom} hitStrokeWidth={14 / zoom} lineCap="round" lineJoin="round"
                  onClick={event => { event.cancelBubble = true; }}/>
            {points.map((point, pointIndex) => <Circle key={pointIndex}
                x={point.x} y={point.y} radius={6 / zoom} fill="#22d3ee" stroke="white"
                strokeWidth={2 / zoom} draggable {...drag.bind({
                    key: `wall:${selectedWall.id}:${pointIndex}`,
                    excluded: {type: "wall", wallId: selectedWall.id, pointIndex},
                    onCommit: value => commands.moveWallPoint(selectedWall.id, pointIndex, value),
                    onPreview: value => onPreviewGeometry?.(value
                        ? moveWallPoint(document, selectedWall.id, pointIndex, value) : undefined),
                })}
                onContextMenu={event => {
                    event.evt.preventDefault();
                    event.cancelBubble = true;
                    commands.removeWallPoint(selectedWall.id, pointIndex);
                }}/>) }
            <GeometrySnapPreviewOverlay geometry={document} snap={drag.dragged?.snap} zoom={zoom}/>
        </>;
    }

    if (selectedDoor) {
        const start = gridToImage(drag.pointFor(`door:${selectedDoor.id}:start`, selectedDoor.start),
            document.registration);
        const end = gridToImage(drag.pointFor(`door:${selectedDoor.id}:end`, selectedDoor.end),
            document.registration);
        const appearance = doorAppearance(selectedDoor, zoom);
        return <>
            <Line points={[start.x, start.y, end.x, end.y]} stroke="#22d3ee" dash={appearance.dash}
                  strokeWidth={5 / zoom} lineCap="round" listening={false}/>
            <Line points={doorHitPoints(start, end, zoom)} stroke="rgba(0,0,0,0.001)"
                  strokeWidth={1 / zoom} hitStrokeWidth={16 / zoom} lineCap="butt"
                  onClick={event => { event.cancelBubble = true; }}/>
            {(["start", "end"] as const).map(endpoint => {
                const point = endpoint === "start" ? start : end;
                return <Circle key={endpoint} x={point.x} y={point.y} radius={6 / zoom}
                               fill="#f59e0b" stroke="white" strokeWidth={2 / zoom} draggable {...drag.bind({
                                   key: `door:${selectedDoor.id}:${endpoint}`,
                                   excluded: {type: "door", doorId: selectedDoor.id},
                                   onCommit: value => commands.moveDoorPoint(selectedDoor.id, endpoint, value),
                                   onPreview: value => onPreviewGeometry?.(value
                                       ? moveDoorPoint(document, selectedDoor.id, endpoint, value) : undefined),
                               })}/>;
            })}
            <GeometrySnapPreviewOverlay geometry={document} snap={drag.dragged?.snap} zoom={zoom}/>
        </>;
    }

    if (selectedTransition) {
        const points = selectedTransition.points.map((point, index) => gridToImage(
            drag.pointFor(`transition:${selectedTransition.id}:${index}`, point), document.registration));
        const arrival = targetGeometry
            ? transitionArrivalOnSourceImage(selectedTransition, document, targetGeometry) : undefined;
        const areaCenter = validTransitionPolygon(points) ? transitionAreaCenter(points) : undefined;
        return <>
            <Line points={points.flatMap(point => [point.x, point.y])} closed
                fill="rgba(34,197,94,0.3)" stroke="#22c55e" strokeWidth={4 / zoom}
                listening={false}/>
            {arrival && <TransitionArrivalMarker point={arrival} origin={areaCenter} zoom={zoom}
                label={targetLevelName}/>}
            {points.map((point, index) => <Circle key={index} x={point.x} y={point.y}
                radius={6 / zoom} fill="#22c55e" stroke="white" strokeWidth={2 / zoom}
                draggable {...drag.bind({
                    key: `transition:${selectedTransition.id}:${index}`,
                    excluded: {type: "transition", transitionId: selectedTransition.id},
                    onCommit: value => commands.moveTransitionPoint(selectedTransition.id, index, value),
                })}
                onContextMenu={event => {
                    event.evt.preventDefault();
                    event.cancelBubble = true;
                    commands.removeTransitionPoint(selectedTransition.id, index);
                }}/>) }
            <GeometrySnapPreviewOverlay geometry={document} snap={drag.dragged?.snap} zoom={zoom}/>
        </>;
    }

    return <Group/>;
}
